// src/services/authHub.js
//
// Sign-in for Relationship Compass runs through the DDD Auth Hub
// (apps/auth-hub-setup). The contract, as the hub implements it:
//
//   in:   <hub>/login (or /signup) ?app=relationship-compass&return=<url>
//   back: <url>#token=<hub token>&spokeToken=<ddd-relationship-compass token>
//   out:  <hub>/logout?app=relationship-compass&return=<url>
//
// The hub token signs in to the hub's own Firebase app (authHubAuth in
// services/firebase.js). That session is the person's DDD account: cloud
// backup goes to it (services/hubSync.js) and the coach is called with it
// (services/CoachingService.js), so deleting the account in the hub deletes
// the backup too. Same pattern as MindEase.
//
// The spoke token is not used. The hub still sends one while
// relationship-compass is in its SPOKE_CONFIG (functions/spokeAdminApps.js),
// but this app's own project (ddd-relationship-compass) has no Firebase
// Authentication set up (identitytoolkit answered CONFIGURATION_NOT_FOUND on
// 7 October 2026), so there is nothing to sign in to. Trying it put a 400 in
// the console on every sign-in.
//
// Signing out goes through the hub's /logout too. The hub keeps its own
// session on its own domain, so signing out here alone left the next Log In
// saying "You're already signed in".
//
// The tokens are live credentials, so stashAuthTokensFromUrl() lifts them out
// of the address bar in main.jsx before the analytics rail starts (the same
// guard as partner join codes). They are held in memory only, never storage.

import { signInWithCustomToken } from 'firebase/auth';
import { authHubAuth } from './firebase';

const APP_ID = 'relationship-compass';
const HUB_URL = import.meta.env.VITE_AUTH_HUB_URL || 'https://auth.divedeepdialogues.com';

let pendingTokens = null;

/** Move hub tokens out of the URL (fragment, or query as a fallback). */
export function stashAuthTokensFromUrl() {
  if (typeof window === 'undefined') return;
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  const token = fragment.get('token') || query.get('token');
  const spokeToken = fragment.get('spokeToken') || query.get('spokeToken');
  if (!token && !spokeToken) return;

  // The spoke token is dropped here: it only has to leave the address bar.
  pendingTokens = { token };

  query.delete('token');
  query.delete('spokeToken');
  const search = query.toString();
  const hash = fragment.has('token') || fragment.has('spokeToken') ? '' : window.location.hash;
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + (search ? `?${search}` : '') + hash
  );
}

/** True while a sign-in handed back by the hub is waiting to be completed. */
export function hasPendingHubSignIn() {
  return pendingTokens !== null;
}

/**
 * Finish a sign-in handed back by the hub. Resolves to the hub user, or null
 * when there was nothing to finish. Throws when the hub sent no hub token,
 * the hub app isn't configured, or the token is refused.
 */
export async function completeHubSignIn() {
  if (!pendingTokens) return null;
  const { token } = pendingTokens;
  pendingTokens = null;
  if (!token) {
    throw new Error('The Auth Hub returned no session token.');
  }
  if (!authHubAuth) {
    throw new Error('The Auth Hub is not configured for this build (VITE_USE_AUTH_HUB / VITE_AUTH_HUB_API_KEY).');
  }
  const credential = await signInWithCustomToken(authHubAuth, token);
  return credential.user;
}

/** Send the user to the hub to sign in (or create an account) and come back here. */
export function redirectToHub(action = 'signin') {
  const page = action === 'signup' ? 'signup' : 'login';
  // Return to this page without its fragment: the hub appends #token=…, and
  // a second '#' would hide the tokens from the fragment parser.
  const { origin, pathname, search } = window.location;
  const returnUrl = `${origin}${pathname}${search}`;
  window.location.href = `${HUB_URL}/${page}?app=${APP_ID}&return=${encodeURIComponent(returnUrl)}`;
}

/**
 * End the hub's own session as well, then come back to this app's home page.
 * Call after signing out here. Same route the rest of the fleet uses.
 */
export function redirectToHubLogout() {
  const returnUrl = `${window.location.origin}/`;
  window.location.href = `${HUB_URL}/logout?app=${APP_ID}&return=${encodeURIComponent(returnUrl)}`;
}
